import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { propertyApi, tenantApi, receiptApi, contractApi } from '../services/api';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  ReceiptRefundIcon,
  PlusCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import LanguageDemo from '../components/LanguageDemo';
import StatCard from '../components/StatCard';
import ActionCard from '../components/ActionCard';

const Dashboard = () => {
  const [stats, setStats] = useState({
    properties: 0,
    tenants: 0,
    receipts: 0,
    contracts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const [propertiesRes, tenantsRes, receiptsRes, contractsRes] = await Promise.all([
          propertyApi.getAll(),
          tenantApi.getAll(),
          receiptApi.getAll(),
          contractApi.getAll()
        ]);

        setStats({
          properties: propertiesRes.data.length,
          tenants: tenantsRes.data.length,
          receipts: receiptsRes.data.length,
          contracts: contractsRes.data.length
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);
  
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Welcome to your Rent Receipt Generator dashboard.
        </p>
      </div>
      
      {loading ? (
        <div className="text-center py-8">Loading statistics...</div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Properties"
              value={stats.properties}
              icon={BuildingOfficeIcon}
              linkTo="/properties"
              linkText="View all properties"
              color="bg-blue-500"
            />
            <StatCard
              title="Tenants"
              value={stats.tenants}
              icon={UserGroupIcon}
              linkTo="/tenants"
              linkText="View all tenants"
              color="bg-green-500"
            />
            <StatCard
              title="Receipts Generated"
              value={stats.receipts}
              icon={ReceiptRefundIcon}
              linkTo="/receipts"
              linkText="View all receipts"
              color="bg-purple-500"
            />
            <StatCard
              title="Contracts"
              value={stats.contracts}
              icon={DocumentTextIcon}
              linkTo="/contracts"
              linkText="View all contracts"
              color="bg-indigo-500"
            />
          </div>

          {/* Quick Actions */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ActionCard
              title="Add Property"
              description="Register a new property in the system"
              icon={BuildingOfficeIcon}
              linkTo="/properties/new"
              buttonText="Add Property"
            />
            <ActionCard
              title="Add Tenant"
              description="Register a new tenant for a property"
              icon={UserGroupIcon}
              linkTo="/tenants/new"
              buttonText="Add Tenant"
            />
            <ActionCard
              title="Generate Receipt"
              description="Create a new rent receipt for a tenant"
              icon={ReceiptRefundIcon}
              linkTo="/receipts/new"
              buttonText="Generate Receipt"
            />
            <ActionCard
              title="Contrat de Location"
              description="Logement nu (non meublé) - France uniquement"
              icon={DocumentTextIcon}
              linkTo="/contracts/new"
              buttonText="Créer un Contrat"
            />
          </div>
          
          {/* Direct button as an alternative */}
          <div className="mt-8 text-center">
            <Link
              to="/properties/new"
              className="btn-primary inline-flex items-center"
            >
              <PlusCircleIcon className="h-5 w-5 mr-1" />
              Add New Property
            </Link>
          </div>
          
          {/* Language features demo */}
          <LanguageDemo />
        </>
      )}
    </div>
  );
};

export default Dashboard;